import React, { useEffect, useImperativeHandle, useState } from 'react';
import type { IFloatingControls, IFloatingRef } from './interfaces';

interface IFloatingControlsResult {
  open: boolean,
  onOpenChange: (open: boolean) => void,
}

/**
 * Open state of a floating element: controlled when `open` and `setOpen` are passed, local otherwise.
 * Exposes `show`/`hide`/`isOpen` through the forwarded ref.
 */
export const useFloatingControls = (
  ref: React.ForwardedRef<IFloatingRef>,
  {
    open: openProp,
    setOpen: setOpenProp,
    afterOpen,
    afterClose,
    shouldOpen = () => true,
    disabled,
  }: IFloatingControls,
): IFloatingControlsResult => {
  const [localOpen, setLocalOpen] = useState(false);
  const open = openProp ?? localOpen;
  const setOpen = setOpenProp ?? setLocalOpen;

  const show = (): void => {
    setOpen(true);
    afterOpen?.();
  };

  const hide = (): void => {
    setOpen(false);
    afterClose?.();
  };

  const onOpenChange = (nextOpen: boolean): void => {
    if (!nextOpen) {
      hide();
    } else if (shouldOpen()) {
      show();
    }
  };

  useImperativeHandle(ref, () => ({ show, hide, isOpen: open }));

  useEffect(() => {
    if (disabled && open) {
      hide();
    }
  }, [disabled]);

  return { open, onOpenChange };
};
